<div class="dialog-title-row">
  <h2 mat-dialog-title>{{ isEditMode ? 'Edit Company' : 'Add Company' }}</h2>
  <button type="button" mat-icon-button aria-label="Close dialog" (click)="close()">
    <mat-icon>close</mat-icon>
  </button>
</div>
<p class="dialog-subtitle">Enter your company details to get started.</p>

<mat-dialog-content>
  <form [formGroup]="form" class="company-form">
    <section class="section-block">
      <h3 class="section-title">COMPANY INFORMATION</h3>
      <div class="form-grid">
        <mat-form-field appearance="outline">
          <mat-label>Legal Name</mat-label>
          <input matInput formControlName="name" placeholder="Enter legal company name" />
          @if (form.get('name')?.hasError('required') && form.get('name')?.touched) {
            <mat-error>
              Legal Name is required
            </mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>DBA (Doing Business As)</mat-label>
          <input matInput formControlName="dba" placeholder="Enter DBA name" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>EIN</mat-label>
          <input matInput formControlName="EIN" placeholder="XX-XXXXXXX" />
          @if (form.get('EIN')?.hasError('required') && form.get('EIN')?.touched) {
            <mat-error>
              EIN is required
            </mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>SIC Code (optional)</mat-label>
          <input matInput formControlName="SIC" placeholder="Enter SIC code (optional)" />
        </mat-form-field>
      </div>
    </section>

    <section class="section-block">
      <h3 class="section-title">ADDRESSES</h3>
      <div class="subsection-title">LEGAL ADDRESS</div>
      <div class="form-grid">
        <mat-form-field appearance="outline">
          <mat-label>Address 1</mat-label>
          <input
            matInput
            formControlName="address1"
            [matAutocomplete]="legalAddressAutocomplete"
            placeholder="Enter address line 1"
            />
          <mat-autocomplete
            #legalAddressAutocomplete="matAutocomplete"
            [displayWith]="displayAddressSuggestion"
            (optionSelected)="onLegalAddressSelected($event)"
            >
            @for (option of legalAddressSuggestions; track option) {
              <mat-option [value]="option">
                {{ option.display }}
              </mat-option>
            }
            @if (isLegalAddressLoading) {
              <mat-option [disabled]="true">Searching addresses...</mat-option>
            }
            @if (!isLegalAddressLoading && !!legalAddressAutocompleteError) {
              <mat-option [disabled]="true">
                {{ legalAddressAutocompleteError }}
              </mat-option>
            }
            @if (!isLegalAddressLoading && !legalAddressAutocompleteError && legalAddressQuery.length >= 3 && legalAddressSuggestions.length === 0) {
              <mat-option
                [disabled]="true"
                >
                No matches found. Try a fuller US street address.
              </mat-option>
            }
          </mat-autocomplete>
          @if (form.get('address1')?.hasError('required') && form.get('address1')?.touched) {
            <mat-error>
              Address 1 is required
            </mat-error>
          }
          <mat-hint>Type at least 3 characters of a US street address</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Address 2</mat-label>
          <input matInput formControlName="address2" placeholder="Apt, suite, unit, building (optional)" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>City</mat-label>
          <input matInput formControlName="city" placeholder="Enter city" />
          @if (form.get('city')?.hasError('required') && form.get('city')?.touched) {
            <mat-error>
              City is required
            </mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>State</mat-label>
          <app-state formControlName="state" [required]="true"></app-state>
          @if (form.get('state')?.hasError('required') && form.get('state')?.touched) {
            <mat-error>
              State is required
            </mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Zip Code</mat-label>
          <input matInput formControlName="postcode" placeholder="Enter zip code" />
          @if (form.get('postcode')?.hasError('required') && form.get('postcode')?.touched) {
            <mat-error>
              Zip Code is required
            </mat-error>
          }
        </mat-form-field>
      </div>

      <mat-checkbox formControlName="headOfficeSameAsLegal" class="inline-checkbox">
        Head office address is the same as legal address
      </mat-checkbox>

      <div class="subsection-title">HEAD OFFICE ADDRESS</div>
      <div class="form-grid">
        <mat-form-field appearance="outline">
          <mat-label>Address 1</mat-label>
          <input
            matInput
            formControlName="headOfficeAddress1"
            [matAutocomplete]="headOfficeAddressAutocomplete"
            placeholder="Enter address line 1"
            />
          <mat-autocomplete
            #headOfficeAddressAutocomplete="matAutocomplete"
            [displayWith]="displayAddressSuggestion"
            (optionSelected)="onHeadOfficeAddressSelected($event)"
            >
            @for (option of headOfficeAddressSuggestions; track option) {
              <mat-option [value]="option">
                {{ option.display }}
              </mat-option>
            }
            @if (isHeadOfficeAddressLoading) {
              <mat-option [disabled]="true">Searching addresses...</mat-option>
            }
            @if (!isHeadOfficeAddressLoading && !!headOfficeAddressAutocompleteError) {
              <mat-option [disabled]="true">
                {{ headOfficeAddressAutocompleteError }}
              </mat-option>
            }
            @if (!isHeadOfficeAddressLoading && !headOfficeAddressAutocompleteError && headOfficeAddressQuery.length >= 3 && headOfficeAddressSuggestions.length === 0) {
              <mat-option
                [disabled]="true"
                >
                No matches found. Try a fuller US street address.
              </mat-option>
            }
          </mat-autocomplete>
          @if (form.get('headOfficeAddress1')?.hasError('required') && form.get('headOfficeAddress1')?.touched) {
            <mat-error>
              Address 1 is required
            </mat-error>
          }
          <mat-hint>Type at least 3 characters of a US street address</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Address 2</mat-label>
          <input matInput formControlName="headOfficeAddress2" placeholder="Apt, suite, unit, building (optional)" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>City</mat-label>
          <input matInput formControlName="headOfficeCity" placeholder="Enter city" />
          @if (form.get('headOfficeCity')?.hasError('required') && form.get('headOfficeCity')?.touched) {
            <mat-error>
              City is required
            </mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>State</mat-label>
          <app-state formControlName="headOfficeState" [required]="true"></app-state>
          @if (form.get('headOfficeState')?.hasError('required') && form.get('headOfficeState')?.touched) {
            <mat-error>
              State is required
            </mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Zip Code</mat-label>
          <input matInput formControlName="headOfficePostcode" placeholder="Enter zip code" />
          @if (form.get('headOfficePostcode')?.hasError('required') && form.get('headOfficePostcode')?.touched) {
            <mat-error>
              Zip Code is required
            </mat-error>
          }
        </mat-form-field>
      </div>
    </section>

    <section class="section-block">
      <h3 class="section-title">CONTACT PERSON</h3>
      <div class="form-grid">
        <mat-form-field appearance="outline">
          <mat-label>Name</mat-label>
          <input matInput formControlName="contactName" placeholder="Enter contact name" />
          @if (form.get('contactName')?.hasError('required') && form.get('contactName')?.touched) {
            <mat-error>
              Name is required
            </mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Title</mat-label>
          <input matInput formControlName="contactTitle" placeholder="Enter title" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Email</mat-label>
          <input matInput formControlName="contactEmail" placeholder="Enter email address" />
          @if (form.get('contactEmail')?.hasError('required') && form.get('contactEmail')?.touched) {
            <mat-error>
              Email is required
            </mat-error>
          }
          @if (form.get('contactEmail')?.hasError('email') && form.get('contactEmail')?.touched) {
            <mat-error>
              Enter a valid email address
            </mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Phone</mat-label>
          <input matInput formControlName="contactPhone" placeholder="(555) 123-4567" />
        </mat-form-field>
      </div>
    </section>

    <section class="section-block">
      <h3 class="section-title">COMPANY LOGO</h3>
      <div class="logo-dropzone">
        <input #logoInput type="file" accept="image/*" class="hidden-input" (change)="onLogoSelected($event)" />

        @if (previewUrl) {
          <img [src]="previewUrl" class="logo-preview" alt="Company logo preview" (error)="clearLogoPreview()" />
        }

        <p>Drag and drop your company logo here, or click to browse.</p>
        <button type="button" mat-stroked-button (click)="onChooseFile(logoInput)">Choose File</button>
        @if (selectedFileName) {
          <small>{{ selectedFileName }}</small>
        }
      </div>
    </section>

    @if (isCompanyThemingFeatureEnabled) {
      <section class="section-block">
        <div class="section-title-row">
          <h3 class="section-title">COMPANY THEME</h3>
          <button
            type="button"
            mat-icon-button
            class="section-help-button"
            [matMenuTriggerFor]="companyThemeHelpMenu"
            aria-label="Open company theme help"
            >
            <mat-icon>help_outline</mat-icon>
          </button>
        </div>
        <p class="section-description">
          Override the default brand colors for this payroll company in Payroll Admin and the customer app.
        </p>
        <mat-slide-toggle formControlName="useCustomTheme" class="theme-toggle">
          Use custom company theme
        </mat-slide-toggle>
        @if (useCustomTheme) {
          <div class="theme-grid">
            <mat-form-field appearance="outline">
              <mat-label>Primary Color</mat-label>
              <input
                matInput
                class="theme-field-input"
                formControlName="ourlyPrimaryColor"
                placeholder="#FF224A"
                [style.--theme-swatch-color]="getThemeControl('ourlyPrimaryColor')?.value || '#ffffff'"
                />
              @if (hasThemeError('ourlyPrimaryColor', 'required')) {
                <mat-error>Primary Color is required</mat-error>
              }
              @if (hasThemeError('ourlyPrimaryColor', 'pattern')) {
                <mat-error>Enter a valid hex color</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Secondary Color</mat-label>
              <input
                matInput
                class="theme-field-input"
                formControlName="ourlySecondaryColor"
                placeholder="#171A51"
                [style.--theme-swatch-color]="getThemeControl('ourlySecondaryColor')?.value || '#ffffff'"
                />
              @if (hasThemeError('ourlySecondaryColor', 'required')) {
                <mat-error>Secondary Color is required</mat-error>
              }
              @if (hasThemeError('ourlySecondaryColor', 'pattern')) {
                <mat-error>Enter a valid hex color</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Tertiary Color</mat-label>
              <input
                matInput
                class="theme-field-input"
                formControlName="wallitTertiaryColor"
                placeholder="#3AE3A0"
                [style.--theme-swatch-color]="getThemeControl('wallitTertiaryColor')?.value || '#ffffff'"
                />
              @if (hasThemeError('wallitTertiaryColor', 'required')) {
                <mat-error>Tertiary Color is required</mat-error>
              }
              @if (hasThemeError('wallitTertiaryColor', 'pattern')) {
                <mat-error>Enter a valid hex color</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Gradient NE Color</mat-label>
              <input
                matInput
                class="theme-field-input"
                formControlName="wallitGradientNEColor"
                placeholder="#FF4139"
                [style.--theme-swatch-color]="getThemeControl('wallitGradientNEColor')?.value || '#ffffff'"
                />
              @if (hasThemeError('wallitGradientNEColor', 'required')) {
                <mat-error>Gradient NE Color is required</mat-error>
              }
              @if (hasThemeError('wallitGradientNEColor', 'pattern')) {
                <mat-error>Enter a valid hex color</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Gradient SW Color</mat-label>
              <input
                matInput
                class="theme-field-input"
                formControlName="wallitGradientSWColor"
                placeholder="#FF005D"
                [style.--theme-swatch-color]="getThemeControl('wallitGradientSWColor')?.value || '#ffffff'"
                />
              @if (hasThemeError('wallitGradientSWColor', 'required')) {
                <mat-error>Gradient SW Color is required</mat-error>
              }
              @if (hasThemeError('wallitGradientSWColor', 'pattern')) {
                <mat-error>Enter a valid hex color</mat-error>
              }
            </mat-form-field>
          </div>
        }
      </section>
    }
  </form>
</mat-dialog-content>

<mat-menu #companyThemeHelpMenu="matMenu" xPosition="before" yPosition="below" class="company-theme-help-menu">
  <div class="company-theme-help-panel" (click)="$event.stopPropagation()">
    <div class="company-theme-help-title">Where each theme color shows up</div>
    <p class="company-theme-help-intro">
      Company theming overrides the active brand colors for this payroll company in Payroll Admin and the customer app.
    </p>
    <div class="company-theme-help-item">
      <strong>Primary Color</strong>
      <span>Main call-to-action buttons, selected states, and the most prominent highlights.</span>
    </div>
    <div class="company-theme-help-item">
      <strong>Secondary Color</strong>
      <span>Supporting buttons, darker accents, and complementary navigation styling.</span>
    </div>
    <div class="company-theme-help-item">
      <strong>Tertiary Color</strong>
      <span>Accent elements, badges, and supporting decorative touches.</span>
    </div>
    <div class="company-theme-help-item">
      <strong>Gradient NE Color</strong>
      <span>The top-right side of branded gradients used in app chrome and visual surfaces.</span>
    </div>
    <div class="company-theme-help-item">
      <strong>Gradient SW Color</strong>
      <span>The bottom-left side of branded gradients used in app chrome and visual surfaces.</span>
    </div>
    <p class="company-theme-help-note">
      Tip: choose colors with strong contrast so text, buttons, and gradient areas stay easy to read.
    </p>
  </div>
</mat-menu>

<mat-dialog-actions align="end" class="footer-actions">
  <button type="button" mat-button (click)="close()">Cancel</button>
  <button
    type="button"
    mat-raised-button
    color="primary"
    (click)="save()"
    >
    {{ isEditMode ? 'Save Changes' : 'Add Company' }}
  </button>
</mat-dialog-actions>
